/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Modal, message, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { Tag } from '~src/services/api/archiveSettings';
import { editTag } from '~src/services/api';

export interface DeleteModalProps extends Partial<ModalProps> {
  onDone?: () => void;
  tag: Tag | null;
}
export const RestoreTagModal: React.FC<DeleteModalProps> = ({
  onCancel,
  onDone,
  tag,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const [doRestoreTag, { isLoading }] = useMutation(editTag, {
    onSuccess: async (deletedTag, {}) => {
      // hide modal
      // @ts-ignore
      onDone ? onDone() : onCancel && onCancel();
      queryCache.invalidateQueries('archiveSettingsList');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'delete-failed' }));
      appLog('RestoreTagModal -> restoreTag -> items', item);
      appLog('RestoreTagModal -> restoreTag -> err', error);
    },
  });

  const handleSubmit = async () => {
    if (tag?.id) {
      doRestoreTag({
        projectId: String(projectId),
        id: String(tag?.id),
        action: 'reactivate',
      });
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'restore' })}
          icon={['fas', 'rotate-left']}
        />
      }
      okText={intl.formatMessage({ id: 'restore' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Typography.Paragraph css={styles.text}>
        {intl.formatMessage(
          { id: 'restore-single-text' },
          {
            name: (tag as Tag)?.title,
          }
        )}
      </Typography.Paragraph>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});
